/** @jsx jsx */
import { jsx, keyframes } from '@emotion/react'
import React, { useEffect, useCallback, useState, useRef } from 'react'
import { NameTag } from './NameTag'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';

type VideoPreviewProps = {
  width?: string
  height?: string
  img?: string
  src?: string
  loopEnd?: number
  name?: string
  sound?: boolean
  hover?: boolean
  label?: boolean
}

// A tile of the team "telco": the photo (or the person icon) is always there; the video lies on top and shows
// once it really plays. Until then a wheel turns; a video that fails or does not start in time leaves the photo.
// The videos are the small versions the server makes on upload (institute/videos.py), about 100 KB each.
const GIVE_UP_MS = 20000

const spin = keyframes`to { transform: rotate(360deg); }`

const fill = (width?: string, height?: string): React.CSSProperties => ({
  width, height, display: 'block',
  objectFit: 'cover',
  objectPosition: 'center 22%', // portraits: the face, not the forehead or the chest
})

const VideoPreview: React.FunctionComponent<VideoPreviewProps> = ({width, height, img, src, loopEnd, name, sound, hover: hoverIn, label}) => {
  const ref = useRef<HTMLDivElement>(null)
  const media = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [failed, setFailed] = useState(false)
  const [waited, setWaited] = useState(false)    // gave up waiting: no wheel any more, the video may still come
  const [photoFailed, setPhotoFailed] = useState(false)
  const [hover, setHover] = useState(false)
  const [muted, setMuted] = useState(true)

  if(label === undefined || label === null) {
    label = true
  }

  useEffect(() => {
    if(hoverIn !== undefined) {
      setHover(hoverIn)
      if(hoverIn && ref.current) {
        ref.current.scrollIntoView({behavior: 'smooth', block: "nearest", inline: "nearest"})
      }
    }
  }, [hoverIn])

  useEffect(() => {
    if (!src) return
    const timer = window.setTimeout(() => setWaited(true), GIVE_UP_MS)
    return () => window.clearTimeout(timer)
  }, [src])

  const timeUpdate = useCallback(() => {
    if (!hover && media.current) {
      if (media.current.currentTime > (loopEnd ?? 3)) {
        media.current.currentTime = 0.01
      }
    }
  }, [hover, loopEnd, media])

  const mouseEnter = useCallback(() => {
    if (!hover) {
      setHover(true)
      setMuted(false)
    }
    if (!hover && media.current) {
      media.current.muted = !sound
    }
  }, [hover, setHover, setMuted, sound, media])

  const mouseLeave = useCallback(() => {
    if (hover) {
      setHover(false)
      setMuted(true)
    }
    if (hover && media.current) {
      media.current.muted = true
      if (media.current.currentTime >= (loopEnd ?? 3)) {
        media.current.currentTime = 0.01
        media.current.play()
      }
    }
  }, [hover, setHover, setMuted, loopEnd, media])

  // a browser may refuse to start it by itself (power saving): then the photo stays, without a wheel
  useEffect(() => {
    const video = media.current
    if (!video || !src) return
    const attempt = video.play()
    if (attempt && attempt.catch) attempt.catch(() => setWaited(true))
  }, [src])

  const showVideo = !!src && !failed
  const photo = img && !photoFailed

  return (
    <div style={{
      width: width,
      height: height,
      position: 'relative',
      display: 'inlineBlock',
      overflow: 'hidden',
      cursor: 'pointer',
      borderRadius: '8px',
      boxShadow: hover? '0 0 10px #0096af' : '',
      margin: '5px 1px 3px 0px',
      border: hover? '1px solid var(--iti-link)' : '1px solid transparent',
      backgroundColor: 'var(--iti-card)',
    }}
      ref={ref}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
    >
      {photo
        ? <img width={width} height={height} src={img} alt="" onError={() => setPhotoFailed(true)} style={fill(width, height)} />
        : <FontAwesomeIcon style={{ width, height, color: 'var(--iti-accent)' }} icon={faUser} />}
      {showVideo && <video
        width={width}
        height={height}
        muted
        autoPlay
        playsInline
        preload="auto"
        onTimeUpdate={timeUpdate}
        onPlaying={() => setPlaying(true)}
        onError={() => setFailed(true)}
        ref={media}
        style={{
          ...fill(width, height),
          position: 'absolute', left: 0, top: 0,
          opacity: playing ? 1 : 0,
          transition: 'opacity .3s ease',
        }}
      >
        {/* an address that does not answer reports its error on the source, not on the video */}
        <source src={src} type='video/mp4' onError={() => setFailed(true)} />
      </video>}
      {showVideo && !playing && !waited && <span
        role="status"
        aria-label="Video lädt"
        css={{
          position: 'absolute', top: '50%', left: '50%',
          width: '22px', height: '22px', margin: label ? '-20px 0 0 -11px' : '-11px 0 0 -11px',
          borderRadius: '50%',
          border: '3px solid rgba(255, 255, 255, .45)',
          borderTopColor: 'var(--iti-link)',
          boxShadow: '0 0 4px rgba(0, 0, 0, .25)',
          animation: `${spin} .9s linear infinite`,
          '@media (prefers-reduced-motion: reduce)': { animationDuration: '2.4s' },
        }}
      />}
      {label && <NameTag name={name ?? "Gast"} muted={muted} />}
    </div>
  )
}
export default VideoPreview
